import { Handle, Node, NodeProps, Position, useUpdateNodeInternals } from "@xyflow/react"
import { Card, CardHeader } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { CheckCircle } from "lucide-react"
import { ReactNode, useCallback, useLayoutEffect, useRef } from "react"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { useWorkflowEnabled } from "../hooks/useWorkflowEnabled"
import { CARD_HEIGHT, CARD_WIDTH } from "../lib/constants"

type WorkflowNodeProps = NodeProps<Node> & {
  isComplete?: boolean
  labels: ReactNode[]
  description: string | ReactNode
  addButton?: ReactNode
  deleteButton?: ReactNode
  warning?: boolean
  hasConditionOutputs?: boolean
}

export function WorkflowNode({
  labels,
  description,
  isComplete,
  addButton,
  deleteButton,
  warning,
  hasConditionOutputs,
  ...node
}: WorkflowNodeProps) {
  const { focusedNode, setFocusedNode } = useWorkflowBuilder()
  const updateNodeInternals = useUpdateNodeInternals()

  const isOpen = focusedNode?.id === node.id

  useLayoutEffect(() => {
    if (hasConditionOutputs) updateNodeInternals(node.id)
  }, [hasConditionOutputs, node.id, updateNodeInternals])

  const workflowId = (node.data as { workflowId?: string } | undefined)?.workflowId
  const isWorkflowDisabled = useWorkflowEnabled(workflowId ?? "") === false

  const flowContainerRef = useRef<HTMLDivElement | null>(null)

  const setFlowContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const flowContainer = node.closest(".react-flow")
      if (flowContainer instanceof HTMLDivElement) {
        flowContainerRef.current = flowContainer
      }
    }
  }, [])

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setFocusedNode(node.id)
  }

  return (
    <div
      className={cn(
        "group relative transition-opacity",
        isOpen && "node-open",
        isWorkflowDisabled && "opacity-50",
      )}
      // IMPORTANT: Do not modify height as the node reposition breaks if all heights are not the same
      style={{ height: CARD_HEIGHT, width: CARD_WIDTH }}
    >
      <Card
        className={cn(
          "relative w-full h-full flex items-start justify-start text-start cursor-pointer overflow-hidden transition-all duration-300 hover:border-foreground/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isOpen && "shadow-xl border-chart-primary hover:border-chart-primary scale-[1.02]",
          warning && "border-warning hover:border-warning",
        )}
        component="button"
        role="button"
        onClick={handleClick}
        onMouseDown={handleClick}
        onPointerDown={handleClick}
        onDragCapture={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        ref={setFlowContainerRef}
      >
        <CardHeader className="w-full flex flex-col items-start gap-1 p-2.5">
          <div className="flex flex-row justify-between items-center gap-2 w-full">
            <div className="flex flex-row gap-2">{labels}</div>
            {isComplete !== undefined && (
              <CheckCircle className={cn("w-5 h-5", isComplete ? "text-positive" : "text-muted")} />
            )}
          </div>
          <div className="flex flex-col px-1 max-w-full w-full">
            {typeof description === "string" ? (
              <span className="text-base text-foreground line-clamp-3">{description}</span>
            ) : (
              description
            )}
          </div>
        </CardHeader>
        <Handle
          id="target"
          type="target"
          position={Position.Top}
          className="bg-transparent! border-0!"
          isConnectable={false}
        />
        {hasConditionOutputs ? (
          <>
            <Handle
              id="Pass"
              type="source"
              position={Position.Bottom}
              className="bg-transparent! border-0!"
              isConnectable={false}
            />
            <Handle
              id="Fail"
              type="source"
              position={Position.Right}
              style={{ top: "50%" }}
              className="bg-transparent! border-0!"
              isConnectable={false}
            />
          </>
        ) : (
          <Handle
            id="source"
            type="source"
            position={Position.Bottom}
            className="bg-transparent! border-0!"
            isConnectable={false}
          />
        )}
        {deleteButton}
      </Card>
      {addButton}
    </div>
  )
}
